import type { KeyboardEvent } from 'react';
import { useCallback, useRef } from 'react';
import type { DraftHandleValue, EditorState } from 'draft-js';
import { getDefaultKeyBinding, RichUtils } from 'draft-js';
import {
  createStateWithNormalText,
  createStateWithTitle,
  createStateWithSubtitle,
} from '../toolbars/buttons/TextSizeButton/utils/utils';
import { getStateWithNewTextAlign } from '../toolbars/buttons/TextAlignButtons/common';
import type { OpenModalRef } from '../../RichTextBox.types';
import { isMac } from '@wix/editor-elements-common-utils';
import { getPossibleKeys } from './keyMapping';

interface CommandsProps {
  editorState: EditorState;
  onChange: (newState: EditorState, prevState: EditorState) => void;
  linkModalButtonRef: React.RefObject<OpenModalRef>;
}

const commandsMap = {
  'set-title': {
    key: '2' as const,
    altKey: true,
    shiftKey: false,
    action: ({ editorState, onChange }: CommandsProps) =>
      onChange(createStateWithTitle(editorState), editorState),
  },
  'set-subtitle': {
    key: '3' as const,
    altKey: true,
    shiftKey: false,
    action: ({ editorState, onChange }: CommandsProps) =>
      onChange(createStateWithSubtitle(editorState), editorState),
  },
  'set-normal-text': {
    key: '0' as const,
    altKey: true,
    shiftKey: false,
    action: ({ editorState, onChange }: CommandsProps) =>
      onChange(createStateWithNormalText(editorState), editorState),
  },
  'toggle-ordered-list': {
    key: '7' as const,
    altKey: false,
    shiftKey: true,
    action: ({ editorState, onChange }: CommandsProps) =>
      onChange(
        RichUtils.toggleBlockType(editorState, 'ordered-list-item'),
        editorState,
      ),
  },
  'toggle-unordered-list': {
    key: '8' as const,
    altKey: false,
    shiftKey: true,
    action: ({ editorState, onChange }: CommandsProps) =>
      onChange(
        RichUtils.toggleBlockType(editorState, 'unordered-list-item'),
        editorState,
      ),
  },
  'align-text-left': {
    key: 'L' as const,
    altKey: false,
    shiftKey: true,
    action: ({ editorState, onChange }: CommandsProps) =>
      onChange(getStateWithNewTextAlign('left', editorState), editorState),
  },
  'align-text-center': {
    key: 'E' as const,
    altKey: false,
    shiftKey: true,
    action: ({ editorState, onChange }: CommandsProps) =>
      onChange(getStateWithNewTextAlign('center', editorState), editorState),
  },
  'align-text-right': {
    key: 'R' as const,
    altKey: false,
    shiftKey: true,
    action: ({ editorState, onChange }: CommandsProps) =>
      onChange(getStateWithNewTextAlign('right', editorState), editorState),
  },
  'align-text-justify': {
    key: 'J' as const,
    altKey: false,
    shiftKey: true,
    action: ({ editorState, onChange }: CommandsProps) =>
      onChange(getStateWithNewTextAlign('justify', editorState), editorState),
  },
  'set-link': {
    key: 'K' as const,
    altKey: false,
    shiftKey: false,
    action: ({ linkModalButtonRef }: CommandsProps) => {
      if (linkModalButtonRef.current?.openModal) {
        linkModalButtonRef.current.openModal();
      }
    },
  },
};

function findCommandByEvent(e: KeyboardEvent) {
  const mainModifierKey = isMac() ? 'metaKey' : 'ctrlKey';

  const [cmd] = Object.entries(commandsMap).find(([, command]) => {
    const possibleKeys = getPossibleKeys(command.key);
    const isKeyMatch = possibleKeys.includes(e.key);
    const isModifierPressed = e[mainModifierKey];
    const isShiftMatch = command.shiftKey === e.shiftKey;
    const isAltMatch = command.altKey === e.altKey;
    return isKeyMatch && isModifierPressed && isShiftMatch && isAltMatch;
  }) || [null, null];

  return cmd;
}

function commandsBindingFn(e: KeyboardEvent): string | null {
  const cmd = findCommandByEvent(e);

  if (cmd) {
    return cmd;
  }

  return getDefaultKeyBinding(e);
}

export function useCommands(
  editorState: EditorState,
  onChange: (newState: EditorState, prevState: EditorState) => void,
) {
  const linkModalButtonRef = useRef<OpenModalRef>({});

  const handleKeyCommand = useCallback(
    (command: string): DraftHandleValue => {
      const newState = RichUtils.handleKeyCommand(editorState, command);
      if (newState) {
        onChange(newState, editorState);
        return 'handled';
      }

      const cmd = commandsMap[command as keyof typeof commandsMap];

      if (cmd) {
        cmd.action({ editorState, onChange, linkModalButtonRef });
        return 'handled';
      }

      return 'not-handled';
    },
    [editorState, onChange],
  );

  return {
    commandsBindingFn,
    handleKeyCommand,
    linkModalButtonRef,
  };
}
